import { useQuery } from '@tanstack/react-query';
import { useState, type JSX } from 'react';

import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api/error';
import { getUserActivityRequest } from '@/services/users';
import type { PaginatedResult } from '@/types/roles';
import type { UserActivityItem } from '@/types/users';

/**
 * Plan 08-02 UD1 UserActivityLog — paginated reverse-chrono LoginHistory
 * projection. 5 columns: Timestamp, Outcome, IP, User Agent, Detail.
 * `userAgent` is truncated to 60 chars for visual hygiene + T-08-18 mitigation
 * (no `dangerouslySetInnerHTML`; React's default escaping handles the rest).
 */

const PAGE_SIZE = 20;

const TS_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

function formatTimestamp(iso: string): string {
  try {
    return TS_FORMATTER.format(new Date(iso));
  } catch {
    return iso;
  }
}

function truncate(s: string | null, max: number): string {
  if (s === null) return '—';
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

function outcomeVariant(outcome: UserActivityItem['outcome']): BadgeProps['variant'] {
  switch (outcome) {
    case 'Success':
    case 'Logout':
      return 'default';
    case 'Failure':
      return 'destructive';
    case 'AdminUnlock':
    case 'AdminPasswordReset':
    case 'ChangePassword':
      return 'secondary';
    default:
      return 'outline';
  }
}

export interface UserActivityLogProps {
  readonly userId: number;
}

export function UserActivityLog({ userId }: UserActivityLogProps): JSX.Element {
  const [page, setPage] = useState<number>(1);

  const query = useQuery<PaginatedResult<UserActivityItem>, ApiError>({
    queryKey: ['user-activity', userId, page, PAGE_SIZE],
    queryFn: () => getUserActivityRequest(userId, page, PAGE_SIZE),
    enabled: Number.isFinite(userId) && userId > 0,
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-3">
      {query.isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="user-activity-loading">
          Loading activity…
        </p>
      )}
      {query.isError && (
        <p className="text-sm text-destructive" data-testid="user-activity-error">
          Failed to load activity log.
        </p>
      )}
      {query.data && (
        <>
          <Table data-testid="user-activity-table">
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>User Agent</TableHead>
                <TableHead>Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <span data-testid="user-activity-empty">
                      No activity recorded for this user.
                    </span>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((it, idx) => (
                  <TableRow key={`${it.timestamp}-${String(idx)}`} data-testid="user-activity-row">
                    <TableCell className="font-mono text-xs">
                      {formatTimestamp(it.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={outcomeVariant(it.outcome)}>{it.outcome}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{it.ipAddress ?? '—'}</TableCell>
                    <TableCell title={it.userAgent ?? undefined}>
                      <span className="text-xs">{truncate(it.userAgent, 60)}</span>
                    </TableCell>
                    <TableCell>{it.detail ?? '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between pt-1">
            <p
              className="text-xs text-muted-foreground"
              data-testid="user-activity-pagination-summary"
            >
              Page {page} of {pageCount} • {total} total
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setPage((p) => Math.max(1, p - 1));
                }}
                disabled={page <= 1}
                data-testid="user-activity-prev"
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setPage((p) => Math.min(pageCount, p + 1));
                }}
                disabled={page >= pageCount}
                data-testid="user-activity-next"
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
